/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "../../Utility/ScrollToTop/ScrollToTop";
import Loader from "../../Utility/Loader/Loader.js";
import CustomBackHeader from "../../Utility/CustomBackHeader/CustomBackHeader.js";
import BackArrow from "../../Utility/CustomBackHeader/BackArrow.js";
import OfferTabFilters from "../../Components/OfferTabFilters/index.jsx";
import FilterModal from "../../Components/FilterModal/FilterModal";
import CommonOffer from "../../Components/CommonWebOffer/CommonOffer.js";
import OfferDetailSheet, { SHEET_TRANSITION_MS } from "./OfferDetailSheet";
import { decryptAES } from "../../Common/Axios/encrypt";
import getTranslation from "../../TranslationUtility/TranslationUtility";
import { GetOfferData } from "../../Store/Slices/dealsDataSlice";
import { dataFromDealPage } from "../../Store/Slices/utilityReducer.js";
import { onLandingHome } from "../../Store/Slices/homePageLandingSlice.js";
import {
  DealCouponFreqData,
  DefaultCardName,
  ClientPlatformCode,
  getDealCategoryNames,
  GetMerchantBgColor,
  IsLightColor,
  ThemeData,
  trackEvent,
  UserIdAndStatusData,
  UtilityReducer,
  ValidateLoginToken,
} from "../../Utility/Utility";

// Inline (not an <img>) so its stroke can follow the pill's `color` via currentColor — the
// icon always matches the hero text colour, including the forced-dark override on a light
// hero background.
const OfferTagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.448 1.33276C7.80162 1.33284 8.14074 1.47338 8.39075 1.72346L14.1939 7.52661C14.495 7.82965 14.664 8.2395 14.664 8.66671C14.664 9.09391 14.495 9.50377 14.1939 9.8068L9.80686 14.1938C9.50383 14.495 9.09397 14.664 8.66677 14.664C8.23957 14.664 7.82971 14.495 7.52667 14.1938L1.72352 8.39069C1.47344 8.14068 1.3329 7.80156 1.33282 7.44794V2.66621C1.33282 2.31256 1.47331 1.97339 1.72338 1.72332C1.97345 1.47325 2.31262 1.33276 2.66627 1.33276H7.448Z"
      stroke="currentColor"
      strokeLinecap="round"
    />
  </svg>
);

const emptyFilters = { validity: [], category: [], brand: [], offer: [] };

// Mirrors FilterModal.js's own getFiltersFromURL — FilterModal already writes
// validity/category/brand/offer as repeated query params on every apply/tab
// click, so a reload should restore the same selection from the URL instead
// of resetting to no filters applied.
const getFiltersFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    validity: params.getAll("validity"),
    category: params.getAll("category"),
    brand: params.getAll("brand"),
    offer: params.getAll("offer"),
  };
};

const MerchantPage = ({ isMobileView }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const translation = getTranslation();
  const login = sessionStorage.getItem("login");

  const token = ValidateLoginToken();
  const { clientCode: clientPlatformCode, platformCode } = ClientPlatformCode();
  const { userId, userStatus } = UserIdAndStatusData();
  const defaultCardName = DefaultCardName();
  const cards = UtilityReducer();
  const creditCards = cards?.creditCards;
  const clientMasterData = useSelector((state) => state.clientMasterReducer);
  const clientCode = clientMasterData?.clientMasterData?.[0]?.clientCode;
  const loginAuthData = useSelector((state) => state.loginAuthReducer);

  const queryParams = new URLSearchParams(location.search);
  const did = queryParams.get("did")?.replace(/\s/g, "+");
  const dealId = did && decryptAES(did);

  const dataFromDealPageData = useSelector((state) => state?.utilityReducer?.dataFromDealPage);
  const deal = dataFromDealPageData?.dealData || {};
  const themeData = ThemeData();
  const merchantBgColor = GetMerchantBgColor(deal, themeData);
  // Hero text/pill colours come in two theme-configured variants (OnDark/OnLight) — pick
  // whichever is readable against the resolved hero background. A light background (light
  // merchantColor/primaryBgColor) also gets a soft bottom shadow, since it would otherwise
  // blend straight into the white page — a dark hero needs none, so that stays unchanged.
  const isHeroBgLight = IsLightColor(merchantBgColor || "#9b1e26");
  const heroStyle = {
    ...(merchantBgColor ? { background: merchantBgColor } : {}),
    // Each carries its own literal fallback so this still degrades correctly even before
    // ApplyThemeStyles has run (e.g. a page reached without the theme-seeding effect — see
    // ThemeData's own fallback comment in Utility.js).
    "--merchant-hero-text": isHeroBgLight ? "var(--merchant-hero-text-on-light, #222222)" : "var(--merchant-hero-text-on-dark, #FFFFFF)",
    "--merchant-hero-pill-bg": isHeroBgLight ? "var(--merchant-hero-pill-bg-on-light, #E7E7E7)" : "var(--merchant-hero-pill-bg-on-dark, #FFFFFF33)",
    ...(isHeroBgLight ? { marginBottom: "1px", boxShadow: "0px 1px 1px 0px var(--merchant-hero-shadow, #0000001A)" } : {}),
  };
  const clientData = clientMasterData?.clientMasterData || [];
  // Same ordering as CouponModal.js: bank-source coupons first, then by
  // displayOrder (entries with a displayOrder sort ahead of those without).
  const rawDealCoupons = dataFromDealPageData?.dealCouponData || [];
  const dealCoupons = [
    ...rawDealCoupons.filter((item) => item.source?.toLowerCase() === "bank"),
    ...rawDealCoupons.filter((item) => item.source?.toLowerCase() !== "bank"),
  ].sort((a, b) => {
    if (a.displayOrder != null && b.displayOrder != null) return a.displayOrder - b.displayOrder;
    if (a.displayOrder != null) return -1;
    if (b.displayOrder != null) return 1;
    return 0;
  });
  const { dealCoupounFrequencies, dealCategories } =
    useSelector((state) => state?.dealsDataSlice?.allofferData) || {};
  const { dealCouponFreqData } = DealCouponFreqData();
  const dealCoupounFrequenciesList = [...(dealCouponFreqData ?? []), ...(dealCoupounFrequencies ?? [])];

  const [appliedFilters, setAppliedFilters] = useState(getFiltersFromURL);
  const [, setTagFilters] = useState(emptyFilters);
  const [activeTab, setActiveTab] = useState("Validity");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [, setIsFetching] = useState(false);

  const [selectedCoupon, setSelectedCoupon] = useState(null);
  // See the scroll-lock effect below — holds the pending delayed-restore timeout plus the
  // true pre-lock body styles, kept outside React state since none of this should ever
  // trigger a re-render.
  const scrollLockStateRef = useRef({ timeoutId: null, previous: null, scrollY: 0 });

  // No pagination on this page, so filtering is done entirely client-side
  // against this one fetch — no need to re-hit the API on every filter
  // change. Same as CouponModal.js: only calls deals-pre-login/deals-post-
  // login when no data is already available for this route (e.g. navigated
  // here from TopOfferPage, which already dispatched this exact merchant's
  // deal + coupons) — otherwise reuse what's already there. When it does
  // fetch, it's scoped server-side via the Brand filter (the API's "Brand"
  // param is actually a deal-id filter), pinned to this dealId instead of
  // searching a generic page for it.
  useEffect(() => {
    if (dealCoupons?.length === 0 || !dealCoupons) {
      if (token?.memberIdentifier || loginAuthData?.data || !loginAuthData) {
        const cardsNetwork = [...new Set(creditCards?.map(card => card.Network?.toLowerCase()).filter(Boolean))] || [];
        const cardCodeValue = [
          ...new Set(
            token?.otherListAttribute?.[0]?.valueDetails
              ?.filter(card => card.status === "1" || card.status === "2")
              ?.map(card => card.CardTypeName?.toLowerCase())
              .filter(Boolean)
          )
        ] || [];
        let payload = {
          search: "",
          filters: { ...emptyFilters, brand: dealId ? [dealId] : [] },
        };

        if (token?.memberIdentifier) payload.memberIdentifier = token.memberIdentifier;
        if (token?.memberIdentifier && cardsNetwork.length > 0) payload.cardsNetwork = cardsNetwork;
        if (token?.memberIdentifier && token?.otherListAttribute[0]?.valueDetails?.length > 0) payload.cardCodeValue = cardCodeValue;
        dispatch(onLandingHome());
        dispatch(GetOfferData(payload)).then((response) => {
          const newDeals = response?.payload?.data?.response?.items[0]?.deals || [];
          const newCoupons = response?.payload?.data?.response?.items[0]?.dealCoupouns || [];
          const matchedDeal = newDeals?.find((d) => d?.id === dealId) || newDeals?.[0];
          const matchedCoupons = newCoupons?.filter((c) => c?.dealId === dealId);
          dispatch(dataFromDealPage({
            dealData: matchedDeal,
            dealCouponData: matchedCoupons
          }));
        });
      }
    }
    // eslint-disable-next-line
  }, []);


  useEffect(() => {
    const state = scrollLockStateRef.current;
    // Closing: the cleanup below (from the just-ended locked effect run) has already scheduled
    // the delayed restore for this same update — leave it alone so it actually fires. Cancelling
    // it here too (it used to run unconditionally) raced against that same-tick scheduling and
    // meant the restore never ran, leaving body permanently locked.
    if (!selectedCoupon) return;

    // (Re)locking — cancel any still-pending delayed restore from a just-closed sheet (the user
    // reopened before it fired) or isMobileView just flipped while still open.
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
      state.timeoutId = null;
    }

    const { body, documentElement } = document;
    // Capture the true pre-lock styles only once. If we're relocking before a rapid close's
    // delayed restore fired, state.previous already holds the real original values — capturing
    // again here would wrongly record the still-locked styles (overflow: hidden, etc.) as
    // "previous", and they'd never actually get restored.
    if (!state.previous) {
      state.scrollY = window.scrollY;
      state.previous = {
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight,
        htmlOverflow: documentElement.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
      };
    }

    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    if (isMobileView) {
      body.style.position = "fixed";
      body.style.top = `-${state.scrollY}px`;
      body.style.width = "100%";
    }

    return () => {
      // Delayed, not immediate — OfferDetailSheet's own close animation (fade/scale) takes
      // SHEET_TRANSITION_MS to finish. Restoring the scrollbar right away (the instant
      // selectedCoupon goes null) makes it reappear mid-animation, shrinking the page's
      // content width and visibly shifting the still-animating sheet left. Waiting for the
      // animation to actually finish first avoids that jump.
      state.timeoutId = setTimeout(() => {
        body.style.overflow = state.previous.bodyOverflow;
        body.style.paddingRight = state.previous.bodyPaddingRight;
        documentElement.style.overflow = state.previous.htmlOverflow;
        body.style.position = state.previous.bodyPosition;
        body.style.top = state.previous.bodyTop;
        body.style.width = state.previous.bodyWidth;
        if (isMobileView) window.scrollTo(0, state.scrollY);
        state.timeoutId = null;
        state.previous = null;
      }, SHEET_TRANSITION_MS);
    };
  }, [selectedCoupon, isMobileView]);

  const categoryLine = getDealCategoryNames(dealCategories, deal?.categoryIds)?.split(", ").filter(Boolean).join(" · ");

  // FilterModal scoped to this merchant: Brand tab is hidden outright (this
  // page is already locked to one merchant), Category limited to the
  // categories this deal is actually mapped to, and Offer limited to the
  // sub-types this deal's own coupons carry (same set driving the
  // OfferTabFilters chip row below).
  const dealCategoryIds = new Set((deal?.categoryIds || []).map(String));
  const categoryOptions = (dealCategories || [])
    .filter((category) => dealCategoryIds.has(String(category?.id)))
    .map((category) => ({ key: category.id, value: category.name }));
  // Offer filter/chips scoped to this merchant's own coupons only — built
  // from their distinct offerSubType values, not the redux-global
  // filteredByOfferSubType list (which spans every merchant).
  const offerSubTypeOptions = Array.from(
    new Set(
      (dealCoupons || [])
        .map((c) => c?.offerSubType)
        .filter((value) => value && String(value).trim() !== "")
    )
  ).map((value) => ({ key: value, value }));

  const offerTabFilterTabs = offerSubTypeOptions.map((item) => ({ key: item?.key, label: item?.value }));

  const filterCount =
    (appliedFilters?.brand?.length || 0) +
    (appliedFilters?.category?.length || 0) +
    (appliedFilters?.validity?.length || 0) +
    (appliedFilters?.offer?.length || 0);

  // Client-side filtering across all three dimensions the Filters modal (+
  // chip row) exposes — no pagination on this page, so the whole set is
  // already loaded and there's no need to round-trip to the API per filter.
  const matchesOffer = (coupon) => {
    if (!appliedFilters?.offer?.length) return true;
    return appliedFilters.offer.some(
      (value) => String(coupon?.offerSubType).toLowerCase() === String(value).toLowerCase()
    );
  };
  const matchesCategory = (coupon) => {
    if (!appliedFilters?.category?.length) return true;
    return appliedFilters.category.some((value) => String(coupon?.category) === String(value));
  };
  // Mirrors DealProvider.GetFilteredData's Today/Weekdays/Weekend grouping
  // on the backend, applied to each coupon's own DealCoupounFrequency
  // WeekDayId array (0=Sunday..6=Saturday, same as JS Date#getDay()).
  const matchesValidity = (coupon) => {
    if (!appliedFilters?.validity?.length) return true;
    const weekDayIds = appliedFilters.validity.map(Number);
    const couponDays = dealCoupounFrequenciesList?.find(
      (freq) => freq?.dealCoupounId === coupon?.id
    )?.weekDayId || [];
    if (!couponDays.length) return false;

    const isToday = weekDayIds.length === 1 && weekDayIds[0] === 1;
    const isWeekdays = (weekDayIds.length === 1 && weekDayIds[0] === 2)
      || (weekDayIds.length === 2 && weekDayIds.includes(1) && weekDayIds.includes(2));
    const isWeekend = weekDayIds.length === 1 && weekDayIds[0] === 3;

    if (isToday) return couponDays.includes(new Date().getDay());
    if (isWeekdays) return couponDays.some((day) => day >= 1 && day <= 5);
    if (isWeekend) return couponDays.some((day) => day === 0 || day === 6);
    return true;
  };

  const filteredDealCoupons = dealCoupons?.filter(
    (coupon) => matchesOffer(coupon) && matchesCategory(coupon) && matchesValidity(coupon)
  );

  // Mirrors TopOfferPage.js's handleOfferTabClick: also syncs the "offer" query
  // param so FilterModal's own Offer checkboxes (seeded from the URL on open)
  // stay in sync with chip taps.
  const handleOfferTabClick = (tab) => {
    const tabKey = String(tab?.key ?? tab?.label ?? "");
    if (!tabKey) return;
    const currentOffer = appliedFilters?.offer || [];
    const nextOffer = currentOffer.includes(tabKey)
      ? currentOffer.filter((value) => value !== tabKey)
      : [...currentOffer, tabKey];
    const nextFilters = { ...appliedFilters, offer: nextOffer };
    setAppliedFilters(nextFilters);
    setTagFilters(nextFilters);

    const url = new URL(window.location.href);
    url.searchParams.delete("offer");
    nextOffer.forEach((value) => url.searchParams.append("offer", value));
    window.history.replaceState({}, "", url.toString());
  };

  const openOfferSheet = (couponData, index) => {
    setSelectedCoupon(couponData);
    trackEvent("select_offer_card", {
      section_title: "Merchant Page",
      image_url: `${process.env.REACT_APP_CLIENT_IMAGE_URL}${deal?.mobImage}`,
      page_type: "merchant_page",
      brand_name: deal?.name,
      item_position: index + 1,
      client_code: clientPlatformCode,
      platform_code: platformCode,
      user_id: userId,
      user_status: userStatus,
      card_name: defaultCardName
    });
  };

  const handleVisitNow = (couponData) => {
    trackEvent("coupon_redeem_click", {
      image_url: `${process.env.REACT_APP_CLIENT_IMAGE_URL}${deal?.commonImage}`,
      title: couponData?.title,
      brand_name: deal?.name,
      page_type: "merchant_page",
      page_url: couponData?.cta,
      section_title: "NA",
      client_code: clientPlatformCode,
      platform_code: platformCode,
      user_id: userId,
      user_status: userStatus,
      card_name: defaultCardName
    });
    if (couponData?.isStaccOffer === true) {
      window.open(couponData?.cta, "_self");
    } else {
      window.open(couponData?.cta, "_blank");
    }
  };

  const navigate = useNavigate();
  const handleDesktopBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <ScrollToTop />
      {!deal?.name ? <Loader /> : (
        <>
          {isMobileView && (
            <CustomBackHeader
              title={translation?.merchantPageTitle}
              customClass="merchant-back-header"
            />
          )}
          <div
            className={`merchant-hero ${clientData[0]?.isBackButton && isMobileView ? "with-back-button" : ""}`}
            style={heroStyle}
          >
            <div id='merchant-hero-container' className="container mx-auto merchant-hero-container">
              {!isMobileView && clientData[0]?.isBackButton && (
                <BackArrow colorClass={isHeroBgLight ? "text-black" : "text-white"} className="merchant-hero-back-desktop" onClick={handleDesktopBackClick} />
              )}

              <div className="merchant-hero-main">
                <div className="merchant-hero-logo" style={merchantBgColor ? { backgroundColor: merchantBgColor } : undefined}>
                  <img
                    src={`${process.env.REACT_APP_CLIENT_IMAGE_URL}${deal?.commonImage}`}
                    alt={deal?.name}
                    title={deal?.name}
                    onError={(e) => (e.target.src = `${process.env.REACT_APP_CLIENT_IMAGE_URL}${clientCode}/img/Placeholder.svg`)}
                  />
                </div>
                <div className="merchant-hero-info">
                  <div>
                    <h1 className="merchant-hero-name">{deal?.name}</h1>
                    {categoryLine && <p className="merchant-hero-category">{categoryLine}</p>}
                  </div>
                  <div className="merchant-hero-pill">
                    <OfferTagIcon />
                    <span>{dealCoupons?.length || 0} {translation?.offers}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="merchant_main_wrapper_container" className="container mx-auto merchant-page">
            <div className="merchant-filters">
              <OfferTabFilters
                tabs={offerTabFilterTabs}
                activeTabs={appliedFilters?.offer || []}
                onTabClick={handleOfferTabClick}
                filterCount={filterCount}
                onFilterClick={() => setIsFilterModalOpen(true)}
                filterIconUrl={`${process.env.REACT_APP_CLIENT_IMAGE_URL}${clientCode}/filter.svg`}
              />
            </div>

            <div className="merchant-offer-grid">
              {filteredDealCoupons?.length > 0 ?
                <>
                  <div className="coupoun-grid-view-wrapper">
                    {
                      filteredDealCoupons.map((couponData, index) => (
                        <CommonOffer
                          key={couponData?.id}
                          index={index}
                          deal={deal}
                          matchingCoupon={[couponData]}
                          handleDealCoupon={() => openOfferSheet(couponData, index)}
                          isMobileView={isMobileView}
                        />
                      ))
                    }
                  </div>
                </> : (
                  <div className="merchant-offer-empty">{translation?.noDataFound}</div>
                )}
            </div>

            {isFilterModalOpen && (
              <FilterModal
                onClose={() => setIsFilterModalOpen(false)}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                setAppliedFilters={setAppliedFilters}
                setTagFilters={setTagFilters}
                setIsFetching={setIsFetching}
                categoryOptions={categoryOptions}
                offerSubTypeOptions={offerSubTypeOptions}
                hideBrandTab
              />
            )}

            <OfferDetailSheet
              coupon={selectedCoupon}
              deal={deal}
              dealCoupounFrequenciesList={dealCoupounFrequenciesList}
              login={login}
              clientCode={clientCode}
              onClose={() => setSelectedCoupon(null)}
              onVisitNow={handleVisitNow}
            />
          </div>
        </>
      )}
    </>
  );
};

export default MerchantPage;
